/*#
# b3d-decorator

**Rocks and trees on the terrain, by budget and by climate.** Drop it into a
scene with a `<tosi-b3d-terrain>`. It scatters up to a set NUMBER of things
around the camera, chosen by [scatter](/scatter/)'s climate rules (the same
temperature, moisture, altitude and slope axes the terrain's biome shader
paints with), so the pines stand where the ground is cold forest, and the
palms at a warm shoreline.

## Demo

```js
import { b3d, b3dSun, b3dSkybox, b3dTerrain, b3dDecorator, slider3d } from 'tosijs-3d'
import { tosi } from 'tosijs'

const { decoratorDemo: s } = tosi({
  decoratorDemo: { season: 0.5, clump: 0.95 },
})

const terrain = b3dTerrain({
  seed: 7,
  biome: 'on',
  surfaceType: 'cylinder',
  radius: 1000,
  cylinderHeight: 1000,
  tileSize: 128,
  lodLevels: 3,
  reach: 3000,
  grossScale: 0.01,
  detailScale: 0.09,
  horizScale: 3.31,
  grossAmplitude: 60,
  detailAmplitude: 10,
  biomeLapseRate: 0.15 / 60,
  biomeTemperature: 0.3,
  biomeMoisture: 0.85,
  biomeSeason: s.season,
  biomeSeasonality: 0.2,
})

preview.append(
  b3d(
    {
      sceneCreated(el, BABYLON) {
        const cam = new BABYLON.FreeCamera('c', new BABYLON.Vector3(0, 60, 0), el.scene)
        cam.setTarget(new BABYLON.Vector3(300, 20, 300))
        cam.maxZ = 20000
        cam.attachControl(el.parts.canvas, true)
        el.setActiveCamera(cam)
        // Stand on the ground once the terrain can say how high it is.
        let placed = false
        el.scene.onBeforeRenderObservable.add(() => {
          const h = terrain.heightSampler?.()
          if (placed || !h) return
          placed = true
          cam.position.y = h(0, 0) + 12
        })
      },
      panelTitle: 'Decorator',
      scenePanel: () => [
        slider3d({ label: 'season', value: s.season, min: 0, max: 1, step: 0.01 }),
        slider3d({ label: 'clump', value: s.clump, min: 0, max: 1, step: 0.05 }),
      ],
    },
    b3dSun({}),
    b3dSkybox({ timeOfDay: 15, realtimeScale: 0 }),
    terrain,
    b3dDecorator({ budget: 4000, radius: 900, clump: s.clump }),
  )
)
```
```css
.preview { height: 100%; }
tosi-b3d { width: 100%; height: 100%; }
```

A temperate valley in autumn. Open the settings panel (the owl): `season`
runs the year, and the leaves turn and fall bare while the pines stay green;
`clump` goes from an even spread to copses with open ground between them.

## What it places, and why it matches the ground

By default: trees, bushes and undergrowth from a curated
[Quaternius](https://quaternius.com) nature library on the CDN, and rocks made
from a seed ([procedural-rock](/procedural-rock/)). Things gather into copses
and rock fields with open ground between them ([scatter](/scatter/)).

Nothing keeps a fixed colour. With a biome terrain, the decorator draws its
materials with the TERRAIN's biome shading, each in a role:

| Role | What it does |
|------|--------------|
| rock (`ground`) | Shaded exactly like terrain: cliff colour on the sides, whatever grows there on top |
| `leaf` | Its own green pulled toward the colour of the ground under it; turns in autumn |
| `evergreen` | The same, but never turns (pines, palms, cacti) |
| `bark` | Its own colour, lightly tinted by the ground |

So changing planet, climate or season recolours the vegetation with the land.
The year is the terrain's: `biomeSeason` and `biomeSeasonality`. Roles are
assigned by material name (`roles`). The default library gets `NATURE_ROLES`;
for any other library set `roles` yourself. A material with no role keeps its
own look.

## How it draws

Every model is drawn with **thin instances**: one draw call per model PART,
however many copies. A few dozen models make a few dozen draw calls, whether
the budget is 1,000 or 20,000. What grows with the budget is
vertices. The Perf Stats panel's `decorator` row shows the counts and the
build time.

The scatter is **world-anchored**, so when the camera moves far enough
(`radius / 4`) it re-scatters around it and only the edge changes. It follows
the terrain's floating origin and rebuilds when the terrain's shape or climate
changes.

## Shadows, where they show

Casting from every copy puts each one into all four shadow cascades:
measured at +3.7 ms (2k) to +6.7 ms (20k) on an M5 Max, most of it for
copies too far away for their shadow to be seen. So each part has a
**shadow-only twin** (on a layer the camera does not draw)
holding just the nearest `shadowBudget` copies within `shadowRange`, re-picked
as the camera moves. The visible copies never cast. Measured after: 5 or 600
casting copies cost the same. What remains (≈2.4 ms there) is the sun's
shadow pass switching on at all, which the sun's own settings govern.

## Collision, where it matters

A small POOL of invisible colliders follows the camera: trunks as thin
cylinders, boulders as boxes (see `ScatterRule.collider`), assigned to the
nearest placements within `colliderRange`. A fixed cost however big the
budget, and it exists exactly where something can bump into it. They collide
the way everything here does (`checkCollisions` + `moveWithCollisions`), so a
biped stops at a trunk and can stand on a boulder.

## The same climate as the ground

The broad noise that makes one region wetter or warmer than the next has to
give the same answer in the ground shader and in the decorator's code, or the
forest would stand where the ground is not drawn green. A shader's hashed noise
cannot be reproduced on the CPU (it depends on how each GPU rounds `sin`), so
the climate noise is a small grid of seeded random bytes: the shader reads it
as a texture, with the fraction eased and the linear filter doing the blend,
and `climateNoise` does the same sum in code.

```javascript
import { climateLattice, climateNoise } from 'tosijs-3d'

const lattice = climateLattice()
const wetter = climateNoise(lattice, x * 0.001, z * 0.001) // about -1…1
```

The grid is 256 cells a side and wraps, so at one cell a kilometre the pattern
repeats every 256 km.

## Attributes

| Attribute | Default | Description |
|-----------|---------|-------------|
| `budget` | `2000` | How many things to place. Performance cares about this, not density |
| `variants` | `0` | Shapes kept per family (a tree species' variants, a rock kind's), each a draw of its own. `0` is auto, from the device tier: all on a desktop, 3 on the medium tier, 2 on the low tier (a Quest) |
| `budgetScale` | `0` | Share of `budget` actually placed. `0` is auto, from the device tier (1, except 0.6 on the low tier, which is what a Quest gets in a session). A smaller share means fewer clumps in the same places, each as full, not thinner clumps. Set `1` to place the whole budget everywhere |
| `radius` | `900` | Metres around the camera to fill |
| `seed` | `1` | Same seed, same forest |
| `url` | `''` | The model library; empty = the curated Quaternius nature library on the CDN (`quaternius/libraries/nature.glb`). Naming another switches the default rules to Kenney's `NATURE_KIT_RULES` |
| `scale` | `1` | Multiplies every rule's scale range |
| `follow` | `'on'` | Re-scatter as the camera moves |
| `shadows` | `'off'` | Cast shadows — from the NEAR copies only (`shadowRange`, `shadowBudget`), through a shadow-only twin of each part. Copies always RECEIVE shadows. Live |
| `colliders` | `'on'` | The nearby collider pool |
| `colliderRange` | `60` | Metres around the camera that get colliders |
| `colliderPool` | `48` | How many colliders at most |
| `shadowRange` | `200` | Metres around the camera whose copies cast shadows (with `shadows: 'on'`) |
| `shadowBudget` | `600` | How many of the nearest copies cast, at most |
| `clear` (property) | `null` | `(x, z) => 0..1`: ground kept clear (0 grows nothing), in the landform's coordinates. A city's `cityGround(...).clear` keeps trees off its streets |
| `clump` | `-1` | How strongly things gather: 0 = even spread, 1 = clumps only. `-1` leaves it to each rule (0.95 by default). Live |
| `clumpSize` | `0` | Metres across a clump and the gap beside it. `0` = auto, about two and a half times the average spacing, so tightening `radius` keeps the clumps. Live |
*/
/*{ "parent": "terrain", "order": 20 }*/

import * as BABYLON from '@babylonjs/core'
import { B3dChild, fetchedUrl, isOff, publicName } from './b3d-utils.js'
import type { B3d } from './tosi-b3d.js'
import { assetUrl } from './asset-url.js'
import { resolveBudget } from './b3d-quality.js'
import { mantaAxes } from './biome-chart.js'
import {
  attachBiomePlugin,
  groundClimateAt,
  type BiomePlugin,
} from './biome-plugin.js'
import { rockFromName, rockGeometry } from './procedural-rock.js'
import {
  isDefaultLibrary,
  limitVariant,
  scatterPlacements,
  NearIndex,
  seatDip,
  pruneScatterCache,
  type GroundSample,
  NATURE_KIT_RULES,
  NATURE_RULES,
  NATURE_ROLES,
  roleFor,
  type DecorationRole,
  type Placement,
  type ScatterRule,
} from './scatter.js'
import { realNow } from './clock.js'

/*
A layer bit the camera does not see (its default mask is 0x0FFFFFFF). The shadow
pass renders its explicit caster list WITHOUT checking layer masks
(ObjectRenderer.forceLayerMaskCheck is false), so a mesh on this layer casts
but is never drawn to the screen.
*/
const SHADOW_ONLY_LAYER = 0x10000000
const DEFAULT_LIBRARY = 'quaternius/libraries/nature.glb'
const NO_ROLES: Record<string, DecorationRole> = {}

interface Part {
  mesh: BABYLON.Mesh
  /** The same geometry, shadow-only, holding just the NEAR copies. */
  shadow: BABYLON.Mesh
  /** Part → model-root transform (the model's own offset removed). */
  rel: BABYLON.Matrix
}

interface ModelInfo {
  parts: Part[]
  /** Model-space bounds (min/max) — sizes the colliders. */
  min: BABYLON.Vector3
  max: BABYLON.Vector3
}

export class B3dDecorator extends B3dChild {
  static preferredTagName = 'tosi-b3d-decorator'
  static shadowStyleSpec = { ':host': { display: 'none' } }

  static initAttributes = {
    budget: 2000,
    // 0 = auto: the device tier's share (see PerfBudgets.decorationScale).
    budgetScale: 0,
    radius: 900,
    seed: 1,
    url: '',
    scale: 1,
    follow: 'on' as 'on' | 'off',
    shadows: 'off' as 'on' | 'off',
    colliders: 'on' as 'on' | 'off',
    colliderRange: 60,
    colliderPool: 48,
    shadowRange: 200,
    shadowBudget: 600,
    // -1 = each rule's own strength; 0…1 overrides them all.
    clump: -1,
    // 0 = auto (from the spacing); otherwise metres.
    clumpSize: 0,
    // Shapes per family (tree species, rock kind); 0 = auto, from the device tier.
    variants: 0,
  }

  declare budget: number
  declare budgetScale: number
  declare variants: number
  declare radius: number
  declare seed: number
  declare url: string
  declare scale: number
  declare follow: 'on' | 'off'
  declare shadows: 'on' | 'off'
  declare colliders: 'on' | 'off'
  declare colliderRange: number
  declare colliderPool: number
  declare shadowRange: number
  declare shadowBudget: number
  declare clump: number
  declare clumpSize: number

  /**
   * Whether the library in use is the default one. Asked of the RESOLVED url,
   * because naming the default library explicitly is still the default
   * library: testing `url` for being set chose Kenney's rules against the
   * Quaternius models, which matched nothing and placed nothing.
   */
  private get _defaultLibrary(): boolean {
    return isDefaultLibrary(
      fetchedUrl(this.url, 'b3d-decorator url'),
      assetUrl(DEFAULT_LIBRARY),
      DEFAULT_LIBRARY
    )
  }

  private _rules: ScatterRule[] | null = null
  /**
   * The rules. Replace before the first build (or call `rebuild()`). Unset,
   * they follow the library: `NATURE_RULES` for the default one, and Kenney's
   * `NATURE_KIT_RULES` when `url` names another (which is what `url` meant
   * before the default library changed).
   */
  get rules(): ScatterRule[] {
    return (
      this._rules ?? (this._defaultLibrary ? NATURE_RULES : NATURE_KIT_RULES)
    )
  }
  /** `null` goes back to the library's own rules. */
  set rules(rules: ScatterRule[] | null) {
    this._rules = rules
  }
  /**
   * Material name → role (`'leaf'`, `'evergreen'` or `'bark'`); see
   * `NATURE_ROLES` for the key forms. A library material with a role is drawn
   * with the terrain's biome shading in that role; any other keeps its own look.
   *
   * Unset, the default library gets `NATURE_ROLES` and any other library gets
   * none: those keys are generic material names (`White`, `Wood`), and a
   * third-party model with a material of the same name had it replaced by
   * flat biome shading. Set `roles` to opt a custom library in.
   */
  get roles(): Record<string, DecorationRole> {
    return this._roles ?? (this._defaultLibrary ? NATURE_ROLES : NO_ROLES)
  }
  set roles(roles: Record<string, DecorationRole>) {
    this._roles = roles
  }
  private _roles: Record<string, DecorationRole> | null = null
  /**
   * Ground kept clear: `(x, z) => 0..1` in logical world coordinates (as a
   * landform's), 0 where nothing grows, 1 where everything may. A city's
   * `cityGround(...).clear` keeps the trees off its streets. Set it, then the
   * next rebuild uses it.
   */
  clear: ((x: number, z: number) => number) | null = null
  /** What was placed last, in LOGICAL world coordinates. */
  placements: Placement[] = []
  /**
   * The NEAR-SET over `placements`: the one query everything near the viewer
   * uses (colliders, shadow casters; LOD, interaction and sound next).
   */
  near: NearIndex<Placement> = new NearIndex([])
  /** Milliseconds the last build took (scatter + instance buffers). */
  lastBuildMs = 0

  private _root: BABYLON.TransformNode | null = null
  private _container: BABYLON.AssetContainer | null = null
  private _models = new Map<string, ModelInfo | null>()
  private _drawn: BABYLON.Mesh[] = []
  private _center: { x: number; z: number } | null = null
  /*
  Ground samples (height + normal), kept across re-scatters: a move samples
  only the ring it entered. Cleared when the terrain's shape changes; the
  climate and rules are re-evaluated every build, so dialling them is cheap
  too.
  */
  private _cache = new Map<number, GroundSample>()
  private _cacheKey = ''
  private _key = ''
  private _observer: BABYLON.Observer<BABYLON.Scene> | null = null
  private _loadGen = 0
  private _pool: BABYLON.Mesh[] = []
  private _poolKind: Array<'trunk' | 'box'> = []
  private _nextColliderCheck = 0
  private _nextShadowCheck = 0

  /*
  SHADOWS, managed HERE rather than through owner.register(). The sun gates
  its casters by the distance of each mesh's ORIGIN from the camera
  (\`activeDistance\`, 80 m in Land and Sky), and a thin-instance part's origin
  is the world origin, not any tree — so the whole set would cast or not by
  how far the camera happened to be from (0, 0, 0). So each part goes straight
  into the sun's shadow generator, and stays there while \`shadows\` is on.
  Re-checked each second: the sun can rebuild its generator.
  */
  private _syncShadows(): void {
    const sun = this.owner?.querySelector('tosi-b3d-sun') as any
    const gen = sun?.shadowGenerator as BABYLON.ShadowGenerator | undefined
    const list = gen?.getShadowMap()?.renderList
    if (gen == null || list == null) return
    const on = !isOff(this.shadows)
    if (!on) this._shadowFrom = null
    for (const info of this._models.values())
      for (const p of info?.parts ?? []) {
        // The VISIBLE copies never cast — only their near-only twins do.
        if (list.includes(p.mesh)) gen.removeShadowCaster(p.mesh)
        const has = list.includes(p.shadow)
        const live = p.shadow.isEnabled() && p.shadow.thinInstanceCount > 0
        if (on && live && !has) gen.addShadowCaster(p.shadow)
        else if ((!on || !live) && has) gen.removeShadowCaster(p.shadow)
      }
  }
  private _debugOff: (() => void) | null = null
  private _measuring = false
  /** Builds completed — measureCost waits on it. */
  private _builds = 0

  sceneReady(owner: B3d, scene: BABYLON.Scene) {
    super.sceneReady(owner, scene)
    this._root = new BABYLON.TransformNode('decorator', scene)
    owner.registerWorldRoot(this._root)
    const gen = ++this._loadGen
    const url =
      fetchedUrl(this.url, 'b3d-decorator url') || assetUrl(DEFAULT_LIBRARY)
    BABYLON.SceneLoader.LoadAssetContainerAsync(url, '', scene)
      .then((c) => {
        if (gen !== this._loadGen) {
          c.dispose()
          return
        }
        this._container = c
        c.addAllToScene()
        // The library's own nodes are the SOURCES, never drawn themselves.
        for (const n of c.rootNodes) n.setEnabled(false)
        this._key = ''
      })
      .catch((err) => console.warn('b3d-decorator: library failed', err))
    this._observer = scene.onBeforeRenderObservable.add(() => this._tick())
    this._debugOff =
      (owner as any).addDebugSource?.({
        name: 'decorator',
        lines: () => [
          `placed ${this.placements.length} / ${this._placedBudget()} (budget ${
            this.budget
          })`,
          `parts ${this._drawn.length} (≈ draw calls)`,
          `build ${this.lastBuildMs.toFixed(0)} ms`,
          `colliders ${this._pool.filter((m) => m.isEnabled()).length}`,
        ],
      }) ?? null
  }

  sceneDispose() {
    this._loadGen++
    if (this._observer != null)
      this.owner?.scene.onBeforeRenderObservable.remove(this._observer)
    this._observer = null
    this._debugOff?.()
    this._debugOff = null
    this._clearParts()
    for (const m of this._pool) m.dispose()
    this._pool = []
    this._poolKind = []
    if (this._root != null) {
      this.owner?.unregisterWorldRoot?.(this._root)
      this._root.dispose()
    }
    this._root = null
    for (const m of this._rockSources) m.dispose()
    this._rockSources = []
    this._rockMaterial?.dispose()
    this._rockMaterial = null
    this._rockBiome = null
    for (const entry of this._roleMaterials.values()) entry.material.dispose()
    this._roleMaterials.clear()
    this._container?.dispose()
    this._container = null
    this._models.clear()
    this.placements = []
    this._cache.clear()
    this._cacheKey = ''
    this._center = null
    this._key = ''
    super.sceneDispose()
  }

  /** Throw away the current scatter and build again on the next frame. */
  rebuild(): void {
    this._key = ''
  }

  private _terrain(): any {
    return this.owner?.querySelector('tosi-b3d-terrain') ?? null
  }

  /** The share of the budget placed here: the attribute, or the device tier's. */
  private _share(): number {
    const xr = (this.owner as any)?.xrActive === true
    return resolveBudget(this.budgetScale, 'decorationScale', { xr })
  }

  /** About how many that is. */
  private _placedBudget(): number {
    return Math.max(0, Math.floor(this.budget * this._share()))
  }

  /** Everything the placements depend on, as one string. */
  private _currentKey(terrain: any): string {
    const p = terrain?.biomePlugin?.params
    return [
      this._placedBudget(),
      this.radius,
      this.seed,
      this.scale,
      this.clump,
      this.clumpSize,
      terrain?.generationKey ?? '',
      terrain?.provinceField != null ? 'province' : '',
      p
        ? [
            p.seaLevel,
            p.baseTemperature,
            p.lapseRate,
            p.mapMoisture,
            p.tNoiseScale,
            p.tNoiseAmp,
            p.mNoiseScale,
            p.mNoiseAmp,
            p.moistureDryHeight,
            p.sheetRadius,
            p.sheetU,
            p.sheetV,
            p.equatorTemp,
            p.temperateTemp,
            p.poleTemp,
            p.rainShadow,
          ].join(',')
        : '',
    ].join('|')
  }

  private _tick(): void {
    const scene = this.owner?.scene
    const cam = scene?.activeCamera
    const terrain = this._terrain()
    if (
      scene == null ||
      cam == null ||
      this._container == null ||
      terrain == null
    )
      return
    if (typeof terrain.heightSampler !== 'function') return
    const off = terrain.originOffset ?? { x: 0, z: 0 }
    const here = {
      x: cam.globalPosition.x + off.x,
      z: cam.globalPosition.z + off.z,
    }
    const key = this._currentKey(terrain)
    const moved =
      !isOff(this.follow) &&
      this._center != null &&
      Math.hypot(here.x - this._center.x, here.z - this._center.z) >
        this.radius * 0.25
    if (key !== this._key || moved) {
      this._key = key
      this._build(terrain, here, off)
    }
    this._syncRockBiome(terrain)
    if (realNow() >= this._nextShadowCheck) {
      this._nextShadowCheck = realNow() + 300
      this._pickShadowCasters(here, off)
      this._syncShadows()
    }
    if (!isOff(this.colliders) && realNow() >= this._nextColliderCheck) {
      this._nextColliderCheck = realNow() + 250
      this._placeColliders(here, off)
    }
  }

  private _warnedMissing = ''
  private _rockSources: BABYLON.Mesh[] = []
  private _rockMaterial: BABYLON.StandardMaterial | null = null
  private _rockBiome: BiomePlugin | null = null

  /** The source mesh for a procedural rock name, or null for any other name. */
  private _rockSource(name: string): BABYLON.Mesh | null {
    const opts = rockFromName(name)
    const scene = this.owner?.scene
    if (opts == null || scene == null) return null
    const rock = rockGeometry(opts)
    const mesh = new BABYLON.Mesh(`rock-source-${name}`, scene)
    const data = new BABYLON.VertexData()
    data.positions = rock.positions
    data.normals = rock.normals
    // The generator winds counter-clockwise (the glTF convention); Babylon's
    // front face is the other way round, so each triangle is reversed here.
    // Unreversed, the rocks drew inside out: only their far walls showed.
    const indices = new Uint16Array(rock.indices.length)
    for (let i = 0; i < indices.length; i += 3) {
      indices[i] = rock.indices[i]
      indices[i + 1] = rock.indices[i + 2]
      indices[i + 2] = rock.indices[i + 1]
    }
    data.indices = indices
    data.applyToMesh(mesh)
    if (this._rockMaterial == null) {
      const mat = new BABYLON.StandardMaterial('deco-rock', scene)
      mat.diffuseColor = new BABYLON.Color3(0.42, 0.4, 0.38)
      mat.specularColor = new BABYLON.Color3(0, 0, 0)
      this._rockMaterial = mat
    }
    mesh.material = this._rockMaterial
    mesh.setEnabled(false)
    mesh.isPickable = false
    this._rockSources.push(mesh)
    return mesh
  }

  private _roleMaterials = new Map<
    string,
    {
      material: BABYLON.StandardMaterial
      plugin: BiomePlugin | null
      role: DecorationRole
    }
  >()

  /**
   * The material a part is DRAWN with. A library material whose name has a
   * role becomes a plain StandardMaterial of the same colour carrying the
   * biome plugin in that role; anything else is used as it came.
   */
  private _roleMaterial(
    src: BABYLON.Material | null,
    model: string
  ): BABYLON.Material | null {
    const scene = this.owner?.scene
    if (src == null || scene == null) return src
    const role = roleFor(this.roles, model, src.name)
    if (role == null) return src
    const key = `${role}-${src.name.replace(/\.\d+$/, '')}`
    const have = this._roleMaterials.get(key)
    if (have != null) return have.material
    const material = new BABYLON.StandardMaterial(`deco-${key}`, scene)
    // A glTF colour is linear; a StandardMaterial's is not.
    const colour = ((src as any).albedoColor ?? (src as any).diffuseColor) as
      | BABYLON.Color3
      | undefined
    material.diffuseColor =
      colour != null
        ? (src as any).albedoColor != null
          ? colour.toGammaSpace()
          : colour.clone()
        : new BABYLON.Color3(0.5, 0.5, 0.5)
    material.specularColor = new BABYLON.Color3(0, 0, 0)
    material.backFaceCulling = src.backFaceCulling
    const entry = { material, plugin: null as BiomePlugin | null, role }
    this._roleMaterials.set(key, entry)
    return material
  }

  /*
  THE ROCKS WEAR THE TERRAIN'S COLOURS. The rock material carries the same
  biome plugin as the ground and reads the SAME params and palettes (by
  reference, re-pointed each frame because a preset replaces them). So a rock's
  steep sides take the terrain's cliff colour and its top takes whatever grows
  there, snow included, and changing planet recolours the rocks with the ground.
  Without a biome terrain they stay plain grey.
  */
  private _syncRockBiome(terrain: any): void {
    const src = terrain?.biomePlugin as BiomePlugin | null
    const live = src != null && src.isEnabled
    const sync = (
      material: BABYLON.Material,
      plugin: BiomePlugin | null,
      role: 'ground' | DecorationRole
    ): BiomePlugin | null => {
      if (!live) {
        if (plugin != null) plugin.isEnabled = false
        return plugin
      }
      plugin ??= attachBiomePlugin(material)
      plugin.role = role
      plugin.isEnabled = true
      plugin.params = src.params
      plugin.palette = src.palette
      plugin.paletteB = src.paletteB
      return plugin
    }
    if (this._rockMaterial != null)
      this._rockBiome = sync(this._rockMaterial, this._rockBiome, 'ground')
    // Leaves and bark: the same climate, read in their own role.
    for (const entry of this._roleMaterials.values())
      entry.plugin = sync(entry.material, entry.plugin, entry.role)
  }

  private _model(name: string): ModelInfo | null {
    if (this._models.has(name)) return this._models.get(name)!
    const c = this._container!
    // A `rock:<kind>:<n>` name is MADE, not looked up (procedural-rock).
    const node =
      this._rockSource(name) ??
      [...c.transformNodes, ...c.meshes].find(
        (n) => publicName(n.name) === name
      )
    let info: ModelInfo | null = null
    if (node != null) {
      /*
      THE glTF ROOT'S FRAME, not the world's. The loader mirrors the whole
      file on \`__root__\` (glTF is right-handed, Babylon left-handed), and
      for an ordinary mesh Babylon undoes the mirror's effect on culling by
      flipping side orientation when the WORLD matrix's determinant is
      negative. A thin instance never gets that flip — Babylon decides from
      the MESH's matrix (identity here), not the instance's — so a mirror
      carried inside the instance matrices rendered every tree inside out.
      Dropping the mirror AND the flip is the same two reversals removed, so
      the winding comes out right; the model is mirrored left-to-right, which
      no tree or rock can show. (Vehicles and library models are converted
      instead: model-transform's unmirrorGltf; here it is not worth the copy.)
      */
      let top: BABYLON.Node = node
      while (top.parent != null) top = top.parent
      const toRoot = (top as BABYLON.TransformNode)
        .computeWorldMatrix(true)
        .clone()
      toRoot.invert()
      node.computeWorldMatrix(true)
      const origin = BABYLON.Vector3.TransformCoordinates(
        node.getAbsolutePosition(),
        toRoot
      )
      const unshift = BABYLON.Matrix.Translation(
        -origin.x,
        -origin.y,
        -origin.z
      )
      const parts: Part[] = []
      const min = new BABYLON.Vector3(Infinity, Infinity, Infinity)
      const max = new BABYLON.Vector3(-Infinity, -Infinity, -Infinity)
      const meshes = [
        ...(node instanceof BABYLON.Mesh ? [node] : []),
        ...node.getChildMeshes(false),
      ].filter(
        (m): m is BABYLON.Mesh =>
          m instanceof BABYLON.Mesh && m.getTotalVertices() > 0
      )
      for (const m of meshes) {
        // The part in root space, the model's own library position removed.
        const rel = m
          .computeWorldMatrix(true)
          .multiply(toRoot)
          .multiply(unshift)
        const part = m.clone(`deco-${name}`, this._root, true) as BABYLON.Mesh
        part.position.setAll(0)
        part.rotationQuaternion = BABYLON.Quaternion.Identity()
        part.scaling.setAll(1)
        part.setEnabled(true)
        part.material = this._roleMaterial(m.material, name)
        part.isPickable = false
        part.checkCollisions = false
        // Trees shade each other, and the terrain's shadows fall on them.
        part.receiveShadows = true
        const shadow = m.clone(
          `deco-${name}-shadow`,
          this._root,
          true
        ) as BABYLON.Mesh
        /*
        ITS OWN GEOMETRY. A clone shares its source's, and a thin-instance
        matrix buffer is registered ON the geometry (world0…world3) — so the
        twin's short list of near casters replaced the visible mesh's buffer.
        The visible mesh then drew its full count from a buffer holding a
        few dozen: WebGL rejects that draw outright (INVALID_OPERATION, no
        other sign), and whole models vanished the moment shadows went on.
        It also made a rebuild flash: everything drew until the next caster
        pick, 300 ms later, took the buffer back.
        */
        shadow.makeGeometryUnique()
        shadow.position.setAll(0)
        shadow.rotationQuaternion = BABYLON.Quaternion.Identity()
        shadow.scaling.setAll(1)
        shadow.layerMask = SHADOW_ONLY_LAYER
        shadow.isPickable = false
        shadow.checkCollisions = false
        shadow.receiveShadows = false
        shadow.setEnabled(false)
        parts.push({ mesh: part, shadow, rel })
        const bb = m.getBoundingInfo().boundingBox
        for (const v of bb.vectorsWorld) {
          const p = BABYLON.Vector3.TransformCoordinates(
            BABYLON.Vector3.TransformCoordinates(v, toRoot),
            unshift
          )
          min.minimizeInPlace(p)
          max.maximizeInPlace(p)
        }
      }
      if (parts.length > 0) info = { parts, min, max }
    }
    this._models.set(name, info)
    return info
  }

  private _clearParts(): void {
    for (const info of this._models.values())
      for (const p of info?.parts ?? []) {
        p.mesh.thinInstanceCount = 0
        p.shadow.thinInstanceCount = 0
        p.shadow.setEnabled(false)
      }
    this._drawn = []
  }

  private _build(
    terrain: any,
    here: { x: number; z: number },
    off: { x: number; z: number }
  ): void {
    const t0 = realNow()
    const height = terrain.heightSampler()
    const cfg = terrain.biomePlugin?.params ?? {
      seaLevel: 0,
      baseTemperature: 0.6,
      lapseRate: 0.004,
      mapMoisture: 0.5,
    }
    const noisy = terrain.biomePlugin != null
    // The cache holds GROUND only, so it is stale only when the terrain is.
    const groundKey = String(terrain?.generationKey ?? '')
    if (groundKey !== this._cacheKey) {
      this._cache.clear()
      this._cacheKey = groundKey
    }
    const rules = this.rules.map((r) => ({
      ...r,
      scale: [r.scale[0] * this.scale, r.scale[1] * this.scale] as [
        number,
        number
      ],
    }))
    let lastX = NaN
    let lastZ = NaN
    let lastFactor = 1
    this.placements = scatterPlacements({
      // The whole budget, thinned by the clump: a device that draws less gets
      // fewer copses in the same places, each as full (see `share`).
      budget: this.budget,
      share: this._share(),
      seed: this.seed,
      center: here,
      radius: this.radius,
      height,
      climate: (x, z, y) => {
        // The climate the ground is drawn with (its regions, and on a
        // sphere world its latitude), so the forest stands where the
        // ground is green.
        const { temperature, moisture } = noisy
          ? groundClimateAt(cfg, x, y, z)
          : mantaAxes(y, cfg)
        return {
          temperature,
          moisture,
          altitude: y - cfg.seaLevel,
          // Land tops out at 0.75 on the chart's moisture axis (mantaAxes).
          wetness: Math.min(1, moisture / 0.75),
        }
      },
      rules,
      clump: Number(this.clump),
      clumpSize: Number(this.clumpSize),
      cache: this._cache,
      /*
      THE PROVINCE SAYS WHAT GROWS: volcanism suppresses plants AND rocks,
      with the same thresholds the biome shader
      uses to paint lava and basalt, so nothing grows where the ground reads
      as rock. Rocks belong on a lava field, but ours cannot read the
      province (it is a per-vertex field on the terrain's tiles), so they
      came out the colour of the country around the volcano: pale stones
      all over black basalt. Until they can, none. Nothing is suppressed
      without a province.
      */
      suppress:
        typeof terrain.provinceField === 'function' || this.clear
          ? (x, z) => {
              // Asked once per plant RULE at the same point: evaluate the
              // province once per point.
              if (x !== lastX || z !== lastZ) {
                lastX = x
                lastZ = z
                lastFactor = 1
                if (typeof terrain.provinceField === 'function') {
                  const v = terrain.provinceField(x, z)
                  const t = Math.max(0, Math.min(1, (v - 0.02) / 0.1))
                  lastFactor = 1 - t * t * (3 - 2 * t)
                }
                if (this.clear)
                  lastFactor *= Math.max(0, Math.min(1, this.clear(x, z)))
              }
              return lastFactor
            }
          : undefined,
    })
    /*
    SEAT THEM ON THE GROUND THAT IS DRAWN, which is not the ground that was
    sampled. `height` is the true surface; what you see is a mesh whose
    vertices are metres apart, and a straight edge between two of them cuts
    UNDER any bump that sits between. A rock placed on the bump floated
    (Tonio: "rocks are often floating above the terrain. We should err towards
    slightly under"). So each placement is lowered by how far the surface
    around it falls below its own tangent plane, one mesh cell out (`seatDip`).
    The cell is the terrain's own (`meshCell`), which counts `horizScale`:
    left out, the correction was a tenth of what it should be in Land and Sky.
    */
    const cell = Number(terrain.meshCell) || 0
    for (const p of this.placements) p.y += seatDip(height, p, cell)
    // Fewer shapes where draws are short: the same places, species and sizes.
    const keep = resolveBudget(Number(this.variants), 'decoVariants')
    if (keep > 0)
      for (const p of this.placements) p.model = limitVariant(p.model, keep)
    this.near = new NearIndex(this.placements, 32)
    pruneScatterCache(this._cache, here, this.radius * 1.5)
    this._center = here
    // The root is where render space starts: reset it, and place in render
    // coordinates for the CURRENT origin. Later shifts move the root.
    if (this._root != null) this._root.position.setAll(0)

    const byModel = new Map<string, Placement[]>()
    for (const p of this.placements) {
      const list = byModel.get(p.model)
      if (list) list.push(p)
      else byModel.set(p.model, [p])
    }
    this._clearParts()
    const missing: string[] = []
    for (const [name, list] of byModel) {
      const info = this._model(name)
      if (info == null) {
        missing.push(name)
        continue
      }
      this._writeInstances(list, info, off, false)
      for (const part of info.parts) this._drawn.push(part.mesh)
    }
    /*
    SAY SO when the rules name models the library does not have. It used to
    place nothing and say nothing, which is exactly what rules written for
    one library do against another (Kenney names against the default
    library, since 0.8.15). Once per distinct set of names.
    */
    const missingKey = missing.sort().join(',')
    if (missing.length > 0 && missingKey !== this._warnedMissing) {
      this._warnedMissing = missingKey
      console.warn(
        `b3d-decorator: ${
          missing.length
        } model name(s) in the rules are not in the library, so nothing is placed for them (first: "${
          missing[0]
        }"). The library is ${
          this.url || `the default '${DEFAULT_LIBRARY}'`
        }. Rules written for Kenney's kit need url set to that kit.`
      )
    }
    this._shadowFrom = null // re-pick the near casters for this build
    this._syncShadows()
    this.lastBuildMs = realNow() - t0
    this._builds++
  }

  private _scratch = {
    up: BABYLON.Vector3.Up(),
    q: new BABYLON.Quaternion(),
    tilt: new BABYLON.Quaternion(),
    yawQ: new BABYLON.Quaternion(),
    n: new BABYLON.Vector3(),
    srt: new BABYLON.Matrix(),
    scl: new BABYLON.Vector3(),
    pos: new BABYLON.Vector3(),
    out: new BABYLON.Matrix(),
  }

  /** One model's copies, into each part's VISIBLE mesh or its SHADOW twin. */
  private _writeInstances(
    list: Placement[],
    info: ModelInfo,
    off: { x: number; z: number },
    shadow: boolean
  ): void {
    const { up, q, tilt, yawQ, n, srt, scl, pos, out } = this._scratch
    const rule = this.rules[list[0].rule]
    const align = rule?.alignToSlope ?? 0
    /*
    ON THE GROUND, not above it. Two corrections, per placement:
    - the model's lowest point goes to the ground, whatever its origin;
    - it SINKS on a slope by footprint radius × tan(the tilt it did not take
      up by leaning), so the downhill side of the base is not in mid-air.
      A trunk's footprint is its trunk, not its canopy; a rock's is itself.
    */
    const width = Math.min(info.max.x - info.min.x, info.max.z - info.min.z)
    const footprint = width * (rule?.collider === 'trunk' ? 0.12 : 0.45)
    const height = info.max.y - info.min.y
    const buried = rule?.sink ?? 0.02
    for (const part of info.parts) {
      const mesh = shadow ? part.shadow : part.mesh
      const buf = new Float32Array(list.length * 16)
      list.forEach((p, i) => {
        BABYLON.Quaternion.RotationYawPitchRollToRef(p.yaw, 0, 0, yawQ)
        n.set(p.normal.x, p.normal.y, p.normal.z)
        BABYLON.Vector3.LerpToRef(up, n, align, n)
        n.normalize()
        BABYLON.Quaternion.FromUnitVectorsToRef(up, n, tilt)
        yawQ.multiplyToRef(tilt, q) // yaw first, then lean to the ground
        scl.setAll(p.scale)
        const slope = Math.acos(Math.min(1, Math.max(-1, p.normal.y)))
        const residual = Math.tan(slope * (1 - align))
        const sink = p.scale * (footprint * residual + height * buried)
        pos.set(p.x - off.x, p.y - info.min.y * p.scale - sink, p.z - off.z)
        BABYLON.Matrix.ComposeToRef(scl, q, pos, srt)
        part.rel.multiplyToRef(srt, out)
        out.copyToArray(buf, i * 16)
      })
      mesh.thinInstanceSetBuffer('matrix', buf, 16, true)
      mesh.thinInstanceRefreshBoundingInfo(false)
    }
  }

  /*
  NEAR-ONLY SHADOWS. Casting from every copy put each one into all four
  cascades — measured at +3.7 ms (2k) to +6.7 ms (20k) on an M5 Max, mostly a
  FIXED cost, and almost all of it for copies too far away for their shadow to
  be seen. So the casters are a separate, shadow-only twin of each part
  holding just the nearest \`shadowBudget\` copies within \`shadowRange\`,
  re-picked when the camera has moved a few metres.
  */
  private _shadowFrom: { x: number; z: number } | null = null
  private _pickShadowCasters(
    here: { x: number; z: number },
    off: { x: number; z: number }
  ): void {
    if (isOff(this.shadows)) return
    if (
      this._shadowFrom != null &&
      Math.hypot(here.x - this._shadowFrom.x, here.z - this._shadowFrom.z) < 5
    )
      return
    this._shadowFrom = here
    const chosen = this.near.near(
      here.x,
      here.z,
      this.shadowRange,
      this.shadowBudget
    )
    const byModel = new Map<string, Placement[]>()
    for (const { item: p } of chosen) {
      const list = byModel.get(p.model)
      if (list) list.push(p)
      else byModel.set(p.model, [p])
    }
    for (const [name, info] of this._models) {
      if (info == null) continue
      const list = byModel.get(name)
      if (list == null) {
        for (const part of info.parts) {
          part.shadow.thinInstanceCount = 0
          part.shadow.setEnabled(false)
        }
        continue
      }
      this._writeInstances(list, info, off, true)
      for (const part of info.parts) part.shadow.setEnabled(true)
    }
    this._syncShadows()
  }

  /*
  THE COLLIDER POOL — invisible primitives on the NEAREST placements that have
  a collider, within colliderRange. Recycled every quarter second.
  */
  private _placeColliders(
    here: { x: number; z: number },
    off: { x: number; z: number }
  ): void {
    const scene = this.owner?.scene
    if (scene == null || this._root == null) return
    const range = this.colliderRange
    const want = this.near
      .near(
        here.x,
        here.z,
        range,
        this.colliderPool,
        (p) => this.rules[p.rule]?.collider != null
      )
      .map(({ item: p, d }) => ({
        p,
        d,
        kind: this.rules[p.rule].collider as 'trunk' | 'box',
      }))
    // Grow the pool as needed; primitives are unit-sized and scaled per use.
    while (this._pool.length < want.length) {
      const i = this._pool.length
      const m = BABYLON.MeshBuilder.CreateBox(
        `deco-collider-${i}`,
        { size: 1 },
        scene
      )
      m.parent = this._root
      m.isVisible = false
      m.isPickable = true
      m.checkCollisions = true
      m.rotationQuaternion = new BABYLON.Quaternion()
      this._pool.push(m)
      this._poolKind.push('box')
    }
    const rootPos = this._root.position
    this._pool.forEach((m, i) => {
      const w = want[i]
      if (w == null) {
        m.setEnabled(false)
        return
      }
      const info = this._model(w.p.model)
      if (info == null) {
        m.setEnabled(false)
        return
      }
      const s = w.p.scale
      const size = info.max.subtract(info.min).scale(s)
      const base = new BABYLON.Vector3(
        w.p.x - off.x,
        w.p.y,
        w.p.z - off.z
      ).subtract(rootPos)
      if (w.kind === 'trunk') {
        // A thin post up the middle: the trunk, not the canopy.
        const r = Math.max(0.15, Math.min(size.x, size.z) * 0.12)
        m.scaling.set(r * 2, size.y * 0.7, r * 2)
        m.position.set(base.x, base.y + size.y * 0.35, base.z)
      } else {
        m.scaling.set(size.x * 0.9, size.y * 0.9, size.z * 0.9)
        m.position.set(base.x, base.y + size.y * 0.45, base.z)
      }
      BABYLON.Quaternion.RotationYawPitchRollToRef(
        w.p.yaw,
        0,
        0,
        m.rotationQuaternion!
      )
      m.setEnabled(true)
    })
  }

  /**
   * WHAT IT COSTS, measured in the browser it runs in. Steps through budgets,
   * lets each build and settle, then samples frames. Restores the budget.
   *
   *   await document.querySelector('tosi-b3d-decorator').measureCost()
   *
   * Frame time is capped by the display's refresh, so when it reads flat the
   * GPU line is the one that shows the cost — where the browser exposes GPU
   * timer queries (often only behind a flag); otherwise watch where the
   * frame time first leaves the refresh interval.
   */
  async measureCost(
    budgets = [0, 1000, 5000, 10000, 20000],
    seconds = 3
  ): Promise<
    Array<{
      budget: number
      placed: number
      buildMs: number
      frameMs: number
      frameP95Ms: number
      gpuMs: number | null
      drawCalls: number
      activeIndices: number
    }>
  > {
    const scene = this.owner?.scene
    if (scene == null) return []
    // ONE at a time: two interleaved runs fight over the budget and every
    // number is garbage (it happened — a timed-out caller's run kept going).
    if (this._measuring) throw new Error('measureCost is already running')
    this._measuring = true
    const owner = this.owner as any
    const engine = scene.getEngine()
    const engineI = new BABYLON.EngineInstrumentation(engine as BABYLON.Engine)
    engineI.captureGPUFrameTime = true
    const sceneI = new BABYLON.SceneInstrumentation(scene)
    const original = this.budget
    /*
    UNTHROTTLED while measuring. <tosi-b3d>'s `frameRate` paces renders (30 by
    default), so frame time would read the throttle, not the load. Lifted to
    whatever the display allows, and restored.
    */
    const originalRate = owner.frameRate
    owner.frameRate = 1000
    const wait = (ms: number) => new Promise((r) => setTimeout(r, ms))
    const results = []
    try {
      for (const budget of budgets) {
        this.budget = budget
        this.rebuild()
        // Wait for THIS budget's build to land, then let it settle.
        const asked = this._builds
        for (let i = 0; i < 100 && this._builds === asked; i++) await wait(50)
        await wait(800)
        const frames: number[] = []
        const gpu: number[] = []
        const draws: number[] = []
        let last = realNow()
        let n = 0
        const obs = scene.onAfterRenderObservable.add(() => {
          const now = realNow()
          if (n++ > 5) {
            frames.push(now - last)
            draws.push(sceneI.drawCallsCounter.current)
            const g = engineI.gpuFrameTimeCounter.current
            if (g > 0) gpu.push(g * 1e-6) // ns → ms
          }
          last = now
        })
        await wait(seconds * 1000)
        scene.onAfterRenderObservable.remove(obs)
        const mean = (a: number[]) =>
          a.reduce((x, y) => x + y, 0) / Math.max(1, a.length)
        const sorted = [...frames].sort((a, b) => a - b)
        results.push({
          budget,
          placed: this.placements.length,
          buildMs: Math.round(this.lastBuildMs),
          frameMs: Number(mean(frames).toFixed(2)),
          frameP95Ms: Number(
            (sorted[Math.floor(sorted.length * 0.95)] ?? 0).toFixed(2)
          ),
          gpuMs: gpu.length ? Number(mean(gpu).toFixed(2)) : null,
          drawCalls: Math.round(mean(draws)),
          activeIndices: scene.getActiveIndices(),
        })
      }
    } finally {
      this.budget = original
      owner.frameRate = originalRate
      this.rebuild()
      sceneI.dispose()
      engineI.dispose()
      this._measuring = false
    }
    return results
  }

  render() {
    super.render()
    // Budget, radius, seed and scale are in the key; changing one rebuilds.
  }
}

export const b3dDecorator = B3dDecorator.elementCreator()
